import React, { useState } from 'react';

import KeyCabinet from '../../media/images/modules/key-cabinet.jpg';
import { ScreenHead } from '../../ui/screen-parts.js';
import { Chip, Field, Kpis, Panel, Empty, useFlash, readForm } from '../../ui/kit.js';
import { day, clock, nextId } from '../../state/building.js';

const KEYS = [
    { id: 1, label: 'Master key M-1', kind: 'Master', holder: 'Luis M. (maintenance)', out: `${day(0)}, 7:40 AM`, reason: 'Unit 803 AC repair' },
    { id: 2, label: 'Master key M-2', kind: 'Master', holder: '', out: '', reason: '' },
    { id: 3, label: 'Unit 1204 key', kind: 'Unit key held', holder: '', out: '', reason: '' },
    { id: 4, label: 'Unit 1502 key', kind: 'Unit key held', holder: 'Sparkle Cleaning Co.', out: `${day(0)}, 9:15 AM`, reason: 'Authorized cleaning, resident away' },
    { id: 5, label: 'Unit 907 key', kind: 'Unit key held', holder: '', out: '', reason: '' },
    { id: 6, label: 'Pool cabana fob C-3', kind: 'Amenity fob', holder: 'Unit 305 (Shah)', out: `${day(0)}, 10:05 AM`, reason: 'Cabana reservation' },
    { id: 7, label: 'Rooftop lounge fob R-1', kind: 'Amenity fob', holder: '', out: '', reason: '' },
    { id: 8, label: 'Electrical room key E-1', kind: 'Contractor key', holder: 'Brightline Electric', out: `${day(-1)}, 2:30 PM`, reason: 'Panel inspection, floors 14 to 18' },
    { id: 9, label: 'Roof access key RF-1', kind: 'Contractor key', holder: '', out: '', reason: '' }
];

const FOBS = [
    { id: 1, number: '40211', resident: 'Alex Rivera', unit: '1204', active: true },
    { id: 2, number: '40388', resident: 'Mariana Costa', unit: '803', active: true },
    { id: 3, number: '40412', resident: 'Daniel Weiss', unit: '1502', active: true },
    { id: 4, number: '40519', resident: 'Priya Shah', unit: '305', active: true },
    { id: 5, number: '40602', resident: 'Robert Klein', unit: '907', active: false }
];

const LOG = [
    { id: 1, at: `${day(0)}, 10:05 AM`, text: 'Pool cabana fob C-3 checked out to Unit 305 (Shah)', by: 'Front desk, Ana' },
    { id: 2, at: `${day(0)}, 9:15 AM`, text: 'Unit 1502 key checked out to Sparkle Cleaning Co.', by: 'Front desk, Ana' },
    { id: 3, at: `${day(0)}, 7:40 AM`, text: 'Master key M-1 checked out to Luis M. (maintenance)', by: 'Front desk, Marcus' },
    { id: 4, at: `${day(-1)}, 8:12 PM`, text: 'Fob 40602 deactivated, reported lost by Unit 907', by: 'Front desk, Marcus' }
];

/* Key / Access Management: every physical key and fob, who holds it, and
   an audit trail the desk never has to write by hand. */
function KeysScreen() {
    const [keys, setKeys] = useState(KEYS);
    const [fobs, setFobs] = useState(FOBS);
    const [log, setLog] = useState(LOG);
    const [flash, notify] = useFlash();

    const record = text => setLog(l => [{ id: nextId(), at: `${day(0)}, ${clock()}`, text, by: 'Front desk, you' }, ...l]);

    const available = keys.filter(k => !k.holder);

    const checkOut = event => {
        const values = readForm(event, ['key', 'holder']);
        if (!values) return notify('Choose a key and who is taking it.');

        const key = keys.find(k => String(k.id) === values.key);
        setKeys(ks => ks.map(k => (k === key ? { ...k, holder: values.holder, out: `${day(0)}, ${clock()}`, reason: values.reason } : k)));
        record(`${key.label} checked out to ${values.holder}${values.reason ? ` (${values.reason})` : ''}`);
        notify(`${key.label} signed out to ${values.holder}.`);
    };

    const giveBack = key => {
        setKeys(ks => ks.map(k => (k.id === key.id ? { ...k, holder: '', out: '', reason: '' } : k)));
        record(`${key.label} returned by ${key.holder}`);
        notify(`${key.label} returned to the key cabinet.`);
    };

    const toggleFob = fob => {
        setFobs(fs => fs.map(f => (f.id === fob.id ? { ...f, active: !f.active } : f)));
        record(fob.active
            ? `Fob ${fob.number} deactivated, reported lost by Unit ${fob.unit}`
            : `Fob ${fob.number} reactivated for Unit ${fob.unit}`);
        notify(fob.active
            ? `Fob ${fob.number} no longer opens any door. ${fob.resident} can pick up a replacement at the desk.`
            : `Fob ${fob.number} is active again.`);
    };

    return (
        <div className="screen-content">
            <ScreenHead eyebrow="Key / access management" title="Keys & access" />

            <div className="banner">
                <img src={KeyCabinet} alt="" />
                <p className="banner__caption">Every key and fob signed out, tracked, and returned.</p>
            </div>

            <Kpis items={[
                { label: 'Keys out', value: keys.length - available.length, tone: 'warn' },
                { label: 'In cabinet', value: available.length, tone: 'ok' },
                { label: 'Masters out', value: keys.filter(k => k.kind === 'Master' && k.holder).length, tone: 'alert' },
                { label: 'Deactivated fobs', value: fobs.filter(f => !f.active).length }
            ]} />

            {flash}

            <div className="split">
                <Panel as="form" className="panel--accent" onSubmit={checkOut}>
                    <h4>Check out a key</h4>
                    <div className="form">
                        <Field label="Key or fob" wide>
                            <select name="key" className="form-control" defaultValue="">
                                <option value="" disabled>Select from the cabinet</option>
                                {available.map(k => <option key={k.id} value={k.id}>{k.label}</option>)}
                            </select>
                        </Field>
                        <Field label="Issued to" wide>
                            <input name="holder" className="form-control" placeholder="e.g. Omar R. or Apex Plumbing" />
                        </Field>
                        <Field label="Reason" wide>
                            <input name="reason" className="form-control" placeholder="e.g. Water heater replacement, unit 1110" />
                        </Field>
                        <button type="submit" className="btn btn-primary">Sign key out</button>
                    </div>
                </Panel>

                <div className="stack">
                    <h4>Key inventory</h4>
                    <ul className="rows">
                        {keys.map(k => (
                            <li className="row" key={k.id}>
                                <div className="row__main">
                                    <span className="row__title">{k.label}</span>
                                    <span className="row__meta">
                                        {k.kind}
                                        {k.holder ? ` · ${k.holder} · since ${k.out}` : ' · in cabinet'}
                                        {k.reason ? ` · ${k.reason}` : ''}
                                    </span>
                                </div>
                                <div className="row__end">
                                    <Chip tone={k.holder ? 'warn' : 'ok'}>{k.holder ? 'Checked out' : 'Available'}</Chip>
                                    {k.holder && (
                                        <button type="button" className="btn btn-outline-light btn-sm" onClick={() => giveBack(k)}>Return</button>
                                    )}
                                </div>
                            </li>
                        ))}
                    </ul>
                </div>
            </div>

            <div className="split section-gap">
                <div className="stack">
                    <h4>Resident fobs</h4>
                    <ul className="rows">
                        {fobs.map(f => (
                            <li className="row" key={f.id}>
                                <div className="row__main">
                                    <span className="row__title">{f.resident}</span>
                                    <span className="row__meta">Unit {f.unit} · fob {f.number}</span>
                                </div>
                                <div className="row__end">
                                    <Chip tone={f.active ? 'ok' : 'alert'}>{f.active ? 'Active' : 'Deactivated'}</Chip>
                                    <button type="button" className="btn btn-outline-light btn-sm" onClick={() => toggleFob(f)}>
                                        {f.active ? 'Deactivate (lost)' : 'Reactivate'}
                                    </button>
                                </div>
                            </li>
                        ))}
                    </ul>
                </div>

                <div className="stack">
                    <h4>Audit log</h4>
                    {log.length === 0 ? <Empty>No key activity yet.</Empty> : (
                        <ul className="rows">
                            {log.map(entry => (
                                <li className="row" key={entry.id}>
                                    <div className="row__main">
                                        <span className="row__title">{entry.text}</span>
                                        <span className="row__meta">{entry.at} · {entry.by}</span>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            </div>
        </div>
    );
}

export default KeysScreen;
